/**
 * Comments API – create and list comments on flows, HLR, LLR, test cases.
 * Tagged users receive notifications.
 */

import apiClient, { TokenManager } from '../../../../shared/lib/apiClient';

export type CommentTargetType =
  | 'user_flow'
  | 'high_level_requirement'
  | 'low_level_requirement'
  | 'test_case';

export interface Comment {
  comment_id: string;
  target_ref: string;
  project_id: string;
  target_type: CommentTargetType;
  target_uiid: string;
  author_user_id: string;
  author_name?: string;
  author_email?: string | null;
  body: string;
  tagged_user_ids: string[];
  created_at: string;
  updated_at?: string;
}

export interface CreateCommentRequest {
  target_type: CommentTargetType;
  target_uiid: string;
  body: string;
  tagged_user_ids?: string[];
  /** Matches API snake_case; helps display when Lambda cannot resolve profile/Cognito. */
  author_display_email?: string;
  author_display_name?: string;
}

export interface ListCommentsResponse {
  comments: Comment[];
}

export async function createComment(
  projectId: string | number,
  request: CreateCommentRequest
): Promise<Comment> {
  const id = String(projectId);
  const userId = TokenManager.getUserId();
  if (!userId) {
    throw new Error('User ID is required to create comments');
  }
  const params = new URLSearchParams({ user_id: userId });
  return apiClient<Comment>(`/comments?${params.toString()}`, {
    method: 'POST',
    body: { project_id: id, ...request },
    requireAuth: true,
  });
}

export async function listComments(
  projectId: string | number,
  targetType: CommentTargetType,
  targetUiid: string
): Promise<ListCommentsResponse> {
  const id = String(projectId);
  const userId = TokenManager.getUserId();
  if (!userId) {
    throw new Error('User ID is required to load comments');
  }
  const params = new URLSearchParams({
    user_id: userId,
    project_id: id,
    target_type: targetType,
    target_uiid: targetUiid,
  });
  return apiClient<ListCommentsResponse>(`/comments?${params.toString()}`, {
    method: 'GET',
    requireAuth: true,
    redirectToLoginOn401: false,
  });
}
